'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface SigPosition {
  // Normalized 0-1 relative to PDF page dimensions
  xRatio: number;
  yRatio: number;
}

interface ResultInfo {
  url: string;
  size: number;
  filename: string;
}

const SIG_WIDTH_PT = 180; // signature width in PDF points
const SIG_HEIGHT_PT = 70; // signature height in PDF points

export default function AddSignatureTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [targetPage, setTargetPage] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResultInfo | null>(null);
  const [sigPosition, setSigPosition] = useState<SigPosition>({ xRatio: 0.5, yRatio: 0.85 });
  const [previewRendering, setPreviewRendering] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);

  // PDF page dimensions in points (for accurate placement)
  const pdfPageDimsRef = useRef<{ width: number; height: number }>({ width: 595, height: 842 });

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sigPadRef = useRef<import('signature_pad').default | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pdfBytesRef = useRef<ArrayBuffer | null>(null);

  // Init signature pad
  useEffect(() => {
    let mounted = true;
    const initPad = async () => {
      const SignaturePad = (await import('signature_pad')).default;
      const canvas = canvasRef.current;
      if (!canvas || !mounted) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.scale(dpr, dpr);
      sigPadRef.current = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255,255,255)',
        penColor: '#0F172A',
        minWidth: 1.5,
        maxWidth: 3,
      });
      sigPadRef.current.addEventListener('endStroke', () => setHasSignature(true));
    };
    initPad();
    return () => { mounted = false; };
  }, []);

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setHasSignature(false);
  };

  const renderPreviewPage = useCallback(async (pageNum: number) => {
    if (!pdfBytesRef.current) return;
    setPreviewRendering(true);
    setPreviewReady(false);
    try {
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.min.mjs`;
      const loadingTask = pdfjsLib.getDocument({ data: pdfBytesRef.current.slice(0) });
      const pdfDoc = await loadingTask.promise;
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.0 });
      pdfPageDimsRef.current = { width: viewport.width, height: viewport.height };

      const canvas = previewCanvasRef.current;
      if (!canvas) return;

      // Scale to fit container (max ~400px wide)
      const maxW = 400;
      const scale = Math.min(maxW / viewport.width, 1.5);
      const scaledViewport = page.getViewport({ scale });
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      await page.render({ canvasContext: ctx, viewport: scaledViewport }).promise;
      setPreviewReady(true);
    } catch (e) {
      console.error('Preview render error', e);
    } finally {
      setPreviewRendering(false);
    }
  }, []);

  const loadPDF = async (f: File) => {
    if (f.type !== 'application/pdf') {
      setError('Please upload a valid PDF file.');
      return;
    }
    setError(null);
    setResult(null);
    setFile(f);
    try {
      const buf = await f.arrayBuffer();
      pdfBytesRef.current = buf;
      const { PDFDocument } = await import('pdf-lib');
      const pdf = await PDFDocument.load(buf);
      const count = pdf.getPageCount();
      setPageCount(count);
      setTargetPage(1);
      setSigPosition({ xRatio: 0.5, yRatio: 0.85 });
      await renderPreviewPage(1);
    } catch {
      setPageCount(1);
    }
  };

  // Re-render preview when page changes
  useEffect(() => {
    if (file && targetPage > 0) {
      renderPreviewPage(targetPage);
    }
  }, [targetPage, file, renderPreviewPage]);

  // Handle click on PDF preview to set signature position
  const handlePreviewClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const rect = overlay.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const xRatio = Math.max(0, Math.min(1, x / rect.width));
    const yRatio = Math.max(0, Math.min(1, y / rect.height));
    setSigPosition({ xRatio, yRatio });
  };

  // Compute pixel position of signature marker on preview
  const getMarkerStyle = () => {
    const canvas = previewCanvasRef.current;
    if (!canvas) return {};
    const canvasW = canvas.width;
    const canvasH = canvas.height;
    const { width: pdfW, height: pdfH } = pdfPageDimsRef.current;
    const scaleX = canvasW / pdfW;
    const scaleY = canvasH / pdfH;
    const sigPxW = SIG_WIDTH_PT * scaleX;
    const sigPxH = SIG_HEIGHT_PT * scaleY;
    const centerX = sigPosition.xRatio * canvasW;
    const centerY = sigPosition.yRatio * canvasH;
    return {
      left: `${centerX - sigPxW / 2}px`,
      top: `${centerY - sigPxH / 2}px`,
      width: `${sigPxW}px`,
      height: `${sigPxH}px`,
    };
  };

  const handleSign = async () => {
    if (!file || !sigPadRef.current || sigPadRef.current.isEmpty()) {
      setError('Please draw your signature first.');
      return;
    }
    setProcessing(true);
    setProgress(0);
    setProgressLabel('Loading PDF…');
    setError(null);
    setResult(null);

    try {
      const { PDFDocument } = await import('pdf-lib');
      const buf = await file.arrayBuffer();
      setProgress(20);
      const pdf = await PDFDocument.load(buf);
      const pages = pdf.getPages();
      const page = pages[targetPage - 1];
      const { width, height } = page.getSize();

      setProgressLabel('Embedding signature…');
      setProgress(50);

      const sigDataUrl = sigPadRef.current.toDataURL('image/png');
      const sigBase64 = sigDataUrl.split(',')[1];
      const sigBytes = Uint8Array.from(atob(sigBase64), (c) => c.charCodeAt(0));
      const sigImage = await pdf.embedPng(sigBytes);

      // Convert normalized position to PDF coordinates
      // PDF y-axis is bottom-up, so invert yRatio
      const centerX = sigPosition.xRatio * width;
      const centerY = (1 - sigPosition.yRatio) * height;
      const sigW = SIG_WIDTH_PT;
      const sigH = SIG_HEIGHT_PT;

      page.drawImage(sigImage, {
        x: centerX - sigW / 2,
        y: centerY - sigH / 2,
        width: sigW,
        height: sigH,
      });

      setProgressLabel('Saving PDF…');
      setProgress(85);
      const bytes = await pdf.save();
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setProgress(100);
      setProgressLabel('Done!');
      setResult({ url, size: bytes.byteLength, filename: `signed_${file.name}` });
    } catch (err) {
      console.error(err);
      setError('Failed to add signature. The PDF may be encrypted or corrupted.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.filename;
    a.click();
  };

  const handleReset = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setFile(null);
    setPageCount(0);
    setResult(null);
    setError(null);
    setProgress(0);
    setProgressLabel('');
    setPreviewReady(false);
    pdfBytesRef.current = null;
    clearSignature();
  };

  return (
    <div className="space-y-5">
      {/* Upload Zone */}
      {!file ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) loadPDF(f);
          }}
        >
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">✍️</span>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadPDF(f); }}
          />
        </div>
      ) : (
        <div className="space-y-5">
          {/* File Card */}
          <div className="flex items-center gap-3 bg-surface rounded-xl px-4 py-3 border border-gray-100">
            <div className="w-9 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[8px] font-black text-red-500">PDF</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
              <p className="text-xs text-text-muted">{formatBytes(file.size)} · {pageCount} {pageCount === 1 ? 'page' : 'pages'}</p>
            </div>
            <button onClick={handleReset} className="text-text-muted hover:text-red-500 transition-colors text-xs font-medium">Remove</button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <span className="text-red-500 mt-0.5 flex-shrink-0">⚠️</span>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Result */}
          {result ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center space-y-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <span className="text-2xl">✅</span>
              </div>
              <div>
                <p className="font-semibold text-green-800">Signature Added!</p>
                <p className="text-sm text-green-700 mt-0.5">Output size: {formatBytes(result.size)}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <button onClick={handleDownload} className="btn-primary px-6 py-2.5 text-sm">
                  ⬇ Download Signed PDF
                </button>
                <button onClick={handleReset} className="px-6 py-2.5 text-sm rounded-xl border border-gray-200 text-text-primary hover:bg-gray-50 transition-colors font-medium">
                  New File
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Page Selector */}
              {pageCount > 1 && (
                <div>
                  <label className="block font-semibold text-text-primary text-sm mb-2">Select Page to Sign</label>
                  <select
                    value={targetPage}
                    onChange={(e) => setTargetPage(Number(e.target.value))}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue"
                  >
                    {Array.from({ length: pageCount }, (_, i) => (
                      <option key={i + 1} value={i + 1}>Page {i + 1}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* PDF Preview with click-to-position */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-semibold text-text-primary text-sm">Click to Position Signature</label>
                  <span className="text-xs text-text-muted bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">Page {targetPage}</span>
                </div>
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-100 relative">
                  {previewRendering && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
                        <span className="text-xs text-text-muted">Rendering page…</span>
                      </div>
                    </div>
                  )}
                  <div
                    ref={overlayRef}
                    className="relative cursor-crosshair"
                    onClick={handlePreviewClick}
                    style={{ lineHeight: 0 }}
                  >
                    <canvas ref={previewCanvasRef} className="w-full" />
                    {/* Signature position marker */}
                    {previewReady && (
                      <div
                        className="absolute border-2 border-dashed border-brand-blue bg-blue-100/40 rounded pointer-events-none flex items-center justify-center"
                        style={getMarkerStyle()}
                      >
                        <span className="text-[10px] text-brand-blue font-semibold select-none">Signature</span>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1.5 text-center">
                  Click anywhere on the page to reposition the signature
                </p>
              </div>

              {/* Signature Pad */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-semibold text-text-primary text-sm">Draw Your Signature</label>
                  <button
                    onClick={clearSignature}
                    className="text-xs text-text-muted hover:text-red-500 transition-colors font-medium"
                  >
                    Clear
                  </button>
                </div>
                <div
                  className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white"
                  style={{ height: 160 }}
                >
                  <canvas
                    ref={canvasRef}
                    className="w-full h-full"
                    style={{ touchAction: 'none' }}
                  />
                </div>
                {!hasSignature && (
                  <p className="text-text-muted text-xs mt-1.5 text-center">Draw your signature above using mouse or touch</p>
                )}
                {hasSignature && (
                  <p className="text-green-600 text-xs mt-1.5 text-center font-medium">✓ Signature drawn</p>
                )}
              </div>

              {/* Progress */}
              {processing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-text-muted">
                    <span>{progressLabel}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-blue rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}

              <button
                onClick={handleSign}
                disabled={!hasSignature || processing}
                className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? 'Adding Signature…' : 'Add Signature to PDF'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
